import { DateTime } from "luxon";

export const capitalize = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const roundToTwoDecimalPoints = (value: number): string => {
  return value.toFixed(2);
};

/** A count with the noun that agrees with it: "1 group", "2 groups". */
export const plural = (count: number, one: string, many = `${one}s`): string =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

const FILES_VIEW_PARAMETER_EXCLUDED_KEYS = new Set(["pageNumber", "pageSize"]);

export function filterFilesViewParameters<T extends object>(
  input: T | null | undefined
): Partial<T> {
  if (input === null || input === undefined) return {};
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(input)) {
    if (FILES_VIEW_PARAMETER_EXCLUDED_KEYS.has(key)) continue;
    if (value === null || value === undefined) continue;
    result[key as keyof T] = value as T[keyof T];
  }
  return result;
}

export function isGuid(str: string): boolean {
  // Basic regex check for standard GUID format
  const pattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return pattern.test(str);
}

export const convertUtcToLocal = (utcTime: string, withTime: boolean = true): string => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localTime = DateTime.fromISO(utcTime, { zone: "utc" })
    .setZone(currentTimeZone)
    .toLocal();
  const options: Intl.DateTimeFormatOptions = withTime
    ? {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }
    : {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

  return localTime.toJSDate().toLocaleString(undefined, options);
};

/**
 * Coarse "2 hours ago" phrasing for a UTC timestamp, for places where how long ago matters more than
 * the exact instant. Pair it with {@link convertUtcToLocal} in a tooltip where the instant matters too.
 */
export const formatRelativeToNow = (utcTime: string): string =>
  DateTime.fromISO(utcTime, { zone: "utc" }).toRelative() ?? "";

export const getFormattedDateString = (
  date: string,
  format = "dd/MM/yyyy",
): string => {
  const luxonDate = DateTime.fromISO(date);
  return luxonDate.toFormat(format);
};

export const getDateTimeString = (date: string | undefined, time: string | undefined) => {
  const defaultTime = "00:00";
  if (date) {
    const timeToUse = time || defaultTime;
    const localDateTime = new Date(`${date}T${timeToUse}`);
    return localDateTime.toISOString().slice(0, 19);
  }
  return null;
};

export const formatDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatTime = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};